import { useMemo } from 'react';

import type { ViewedDocumentContext } from '../../../contracts/projects/requests.types';
import type { WorkspaceSnapshotV1 } from '../../../contracts/projects/workspace.types';
import { resolveViewedDocument } from '../../../contracts/projects/workspace/viewedDocument';

/** A session letter, as the viewed-document context reads it. */
interface ViewedLetter {
  readonly body_content?: string | null;
  readonly name?: string | null;
  readonly subject_line?: string | null;
}

/**
 * The app's reads behind the viewed-document context, as hook ports, plus the
 * session tab labels. Each read takes the ids resolved here, an empty id when
 * that body is not on screen.
 */
export interface ViewedDocumentReads {
  readonly tabs: Parameters<typeof resolveViewedDocument>[1];
  readonly useNoteContent: (sessionId: string) => string | null | undefined;
  readonly useSessionDocument: (
    sessionId: string,
    documentId: string
  ) =>
    | { readonly content: string | null; readonly title: string | null }
    | null
    | undefined;
  readonly useSessionLetter: (
    sessionId: string,
    letterId: string,
    enabled: boolean
  ) => ViewedLetter | undefined;
}

/**
 * Live body of the document/tab currently on screen, for Work turn context.
 * Undefined when nothing is actually being viewed.
 */
export function useViewedDocumentContext(
  workspace: WorkspaceSnapshotV1 | undefined,
  { tabs, useNoteContent, useSessionDocument, useSessionLetter }: ViewedDocumentReads
): ViewedDocumentContext | undefined {
  const artifact = workspace?.open_artifact ?? null;
  const sessionId = artifact?.session_id ?? '';
  const tab = artifact?.active_tab;
  const documentId = artifact?.document_id ?? '';
  const letterId = artifact?.letter_id ?? '';
  const wantsNote = artifact?.type === 'note' || tab === tabs.note;
  const wantsDocument =
    artifact?.type === 'document' || tab === tabs.document || tab === tabs.prechart;
  const wantsLetter = artifact?.type === 'letter' || tab === tabs.letter;

  const noteContent = useNoteContent(wantsNote ? sessionId : '');
  const document = useSessionDocument(sessionId, wantsDocument ? documentId : '');
  const letter = useSessionLetter(
    sessionId,
    letterId,
    Boolean(sessionId && letterId && wantsLetter)
  );

  return useMemo(
    () =>
      resolveViewedDocument(
        {
          artifactType: artifact?.type,
          tab,
          documentId,
          letterId,
          noteContent: wantsNote ? noteContent : null,
          documentContent: wantsDocument ? document?.content : null,
          documentTitle: wantsDocument ? document?.title : null,
          letterContent: wantsLetter ? letter?.body_content : null,
          letterTitle: wantsLetter ? (letter?.name ?? letter?.subject_line) : null,
        },
        tabs
      ),
    [
      artifact?.type,
      tab,
      tabs,
      documentId,
      letterId,
      wantsNote,
      wantsDocument,
      wantsLetter,
      noteContent,
      document?.content,
      document?.title,
      letter?.body_content,
      letter?.name,
      letter?.subject_line,
    ]
  );
}
